import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, DollarSign, Calendar, Users, ArrowRight, Search as SearchIcon,
  Loader2, MessageSquare,
} from "lucide-react";

const RESPITE = [
  ["traditional_respite", "Traditional respite"],
  ["overnight_support", "Overnight support"],
  ["travel_assistance", "Travel assistance"],
  ["party_buffers", "Party buffers"],
  ["community_support", "Community support"],
];

const FREQ_LABEL = {
  one_time: "One time",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  occasional: "Occasional",
};

export default function SitterListingsBrowse() {
  const [filters, setFilters] = useState({
    zip_code: "", max_distance_miles: 25,
    respite_services: [], min_hourly_rate: "",
    aba_required: false, overnight_needed: false,
    keyword: "",
  });
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const payload = { ...filters };
      payload.min_hourly_rate = payload.min_hourly_rate === "" ? null : Number(payload.min_hourly_rate);
      payload.max_distance_miles = payload.max_distance_miles === "" ? null : Number(payload.max_distance_miles);
      if (!payload.zip_code) delete payload.zip_code;
      if (!payload.aba_required) delete payload.aba_required;
      if (!payload.overnight_needed) delete payload.overnight_needed;
      if (!payload.keyword) delete payload.keyword;
      const { data } = await api.post("/family-listings/search", payload);
      setResults(data.results || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const toggleService = (s) => setFilters((f) => ({
    ...f,
    respite_services: f.respite_services.includes(s)
      ? f.respite_services.filter((x) => x !== s)
      : [...f.respite_services, s],
  }));

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="sitter-listings-page">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div>
          <span className="section-eyebrow">Families looking for care</span>
          <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Browse family listings</h1>
          <p className="text-stone2-600 text-sm mt-1 max-w-lg">
            Real families near you have posted what kind of specialized care they need. Reach out directly — messaging families is unlimited.
          </p>
        </div>

        <div className="mt-8 grid lg:grid-cols-[280px_1fr] gap-6">
          {/* Filters */}
          <aside className="paper-card p-5 h-fit lg:sticky lg:top-24">
            <h2 className="font-heading font-semibold text-sage-900 mb-3 text-sm uppercase tracking-wider">Filters</h2>
            <div className="space-y-4">
              <div>
                <label className="field-label">Keyword</label>
                <input value={filters.keyword} onChange={(e) => setFilters((f) => ({ ...f, keyword: e.target.value }))}
                  data-testid="filter-listing-keyword" placeholder="overnight, weekends…"
                  className="w-full rounded-lg border border-stone2-200 px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="field-label">Your ZIP</label>
                  <input value={filters.zip_code} onChange={(e) => setFilters((f) => ({ ...f, zip_code: e.target.value }))}
                    data-testid="filter-listing-zip" className="w-full rounded-lg border border-stone2-200 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="field-label">Within mi</label>
                  <input type="number" value={filters.max_distance_miles}
                    onChange={(e) => setFilters((f) => ({ ...f, max_distance_miles: e.target.value }))}
                    data-testid="filter-listing-distance" className="w-full rounded-lg border border-stone2-200 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="field-label">Min rate offered ($/hr)</label>
                <input type="number" value={filters.min_hourly_rate}
                  onChange={(e) => setFilters((f) => ({ ...f, min_hourly_rate: e.target.value }))}
                  data-testid="filter-listing-rate" className="w-full rounded-lg border border-stone2-200 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="field-label">Respite service</label>
                <div className="flex flex-wrap gap-1.5">
                  {RESPITE.map(([k, l]) => (
                    <button type="button" key={k} onClick={() => toggleService(k)}
                      data-testid={`filter-listing-respite-${k}`}
                      className={filters.respite_services.includes(k) ? "chip-ochre" : "chip"}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-sm text-sage-900">
                  <input type="checkbox" checked={filters.aba_required}
                    onChange={(e) => setFilters((f) => ({ ...f, aba_required: e.target.checked }))}
                    data-testid="filter-listing-aba" className="rounded text-sage-600" />
                  ABA training required
                </label>
                <label className="flex items-center gap-2 text-sm text-sage-900">
                  <input type="checkbox" checked={filters.overnight_needed}
                    onChange={(e) => setFilters((f) => ({ ...f, overnight_needed: e.target.checked }))}
                    data-testid="filter-listing-overnight" className="rounded text-sage-600" />
                  Overnight needed
                </label>
              </div>
              <button type="button" onClick={run} disabled={loading} className="btn-primary w-full" data-testid="filter-listing-run">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <SearchIcon className="w-4 h-4" />}
                Apply filters
              </button>
            </div>
          </aside>

          {/* Results */}
          <div>
            <div className="mb-3 text-sm text-stone2-600" data-testid="listing-results-count">
              {loading ? "Searching…" : `${results.length} family listing${results.length === 1 ? "" : "s"} matched`}
            </div>
            <div className="space-y-3">
              {results.length === 0 && !loading && (
                <div className="paper-card p-8 text-center text-stone2-500">
                  No listings match your filters yet. Try widening your distance or clearing filters.
                </div>
              )}
              {results.map((l) => (
                <Link key={l.id} to={`/listings/${l.id}`} data-testid={`listing-result-${l.id}`}
                  className="paper-card p-5 block hover:border-sage-400 hover:-translate-y-0.5 transition-all duration-200">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-wider text-stone2-500 mb-1">
                        {FREQ_LABEL[l.frequency] || l.frequency}
                        {l.distance_miles !== undefined && <> · {l.distance_miles} mi away</>}
                      </div>
                      <h3 className="font-heading font-semibold text-sage-900 text-lg leading-tight">{l.title}</h3>
                      <p className="mt-1 text-sm text-stone2-600 line-clamp-2">{l.description}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-stone2-600">
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {[l.city, l.state].filter(Boolean).join(", ") || "Area"} · {l.zip_code}
                        </span>
                        {l.hourly_rate_offered && (
                          <span className="inline-flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> ${l.hourly_rate_offered}/hr</span>
                        )}
                        {l.schedule_notes && (
                          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {l.schedule_notes}</span>
                        )}
                        {l.child_ages_summary && (
                          <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {l.child_ages_summary}</span>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(l.respite_services_needed || []).map((s) => (
                          <span key={s} className="chip">{(RESPITE.find(([k]) => k === s) || [s, s])[1]}</span>
                        ))}
                        {l.aba_required && <span className="chip-ochre">ABA required</span>}
                        {l.overnight_needed && <span className="chip-ochre">Overnight</span>}
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-sage-700 shrink-0 mt-1" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
